import { useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ChevronRight, Folder as FolderIcon } from '@mui/icons-material';
import { useLazyGetFolderItemsQuery, type FileStorageItem } from '../../api/fileStorageApi';

type PathItem = { id: string | undefined; name: string };

type FolderTreeNodeProps = {
  folder: FileStorageItem;
  depth: number;
  ancestors: PathItem[];
  currentFolderId: string | undefined;
  onNavigate: (path: PathItem[]) => void;
};

export const FolderTreeNode = ({
  folder,
  depth,
  ancestors,
  currentFolderId,
  onNavigate,
}: FolderTreeNodeProps) => {
  const [open, setOpen] = useState(false);
  const [trigger, { data }] = useLazyGetFolderItemsQuery();

  const showChevron = folder.isFolder && folder.hasSubDirectories;

  useEffect(() => {
    if (!open || !folder.isFolder || !folder.hasSubDirectories) return;
    void trigger({ nodeKey: folder.id, storageType: 'All files' });
  }, [open, folder.id, folder.hasSubDirectories, folder.isFolder, trigger]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!showChevron) return;
    setOpen((v) => !v);
  };

  const handleSelect = () => {
    onNavigate([...ancestors, { id: folder.id, name: folder.name }]);
  };

  const subFolders = data?.fileItems.filter((i) => i.isFolder) ?? [];
  const path = [...ancestors, { id: folder.id, name: folder.name }];

  return (
    <>
      <ListItemButton
        dense
        selected={currentFolderId === folder.id}
        onClick={handleSelect}
        sx={{ pl: 1 + depth * 1.5, pr: 0.5, py: 0.5 }}
      >
        <Box sx={{ width: 22, display: 'inline-flex', justifyContent: 'center', mr: 0.5 }}>
          {showChevron && (
            <IconButton
              size="small"
              onClick={handleToggle}
              sx={{
                p: 0.25,
                transition: 'transform 180ms ease',
                transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          )}
        </Box>
        <ListItemIcon sx={{ minWidth: 24 }}>
          <FolderIcon fontSize="small" color="warning" />
        </ListItemIcon>
        <ListItemText
          primary={folder.name}
          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
          sx={{ mr: 0 }}
        />
      </ListItemButton>

      <Collapse in={open} unmountOnExit>
        {subFolders.map((sub) => (
          <FolderTreeNode
            key={sub.id}
            folder={sub}
            depth={depth + 1}
            ancestors={path}
            currentFolderId={currentFolderId}
            onNavigate={onNavigate}
          />
        ))}
      </Collapse>
    </>
  );
};
